import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useGoogleLogin } from '@react-oauth/google';
import {
  Loader2, Phone, Lock, User, Mail, MapPin, Home as HomeIcon,
  ArrowLeft, ArrowRight, Zap, X, Package, CheckCircle, Shirt,
  Bell, Star, Truck, Clock, ShieldCheck, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── CINEMATIC DEMO ────────────────────────────────────────────────────────────

const SCENES = [
  {
    id: 1,
    chapter: 'Scene 01',
    title: 'Open JustFold',
    subtitle: 'Your laundry dashboard',
    glow: 'shadow-orange-200',
    ring: 'ring-orange-200',
    screen: 'dashboard',
  },
  {
    id: 2,
    chapter: 'Scene 02',
    title: 'Book a Pickup',
    subtitle: 'One tap from your home',
    glow: 'shadow-blue-200',
    ring: 'ring-blue-200',
    screen: 'tap',
  },
  {
    id: 3,
    chapter: 'Scene 03',
    title: 'Enter Address',
    subtitle: 'We come to your door',
    glow: 'shadow-violet-200',
    ring: 'ring-violet-200',
    screen: 'address',
  },
  {
    id: 4,
    chapter: 'Scene 04',
    title: '72-hr Delivery',
    subtitle: 'Confirm your order',
    glow: 'shadow-orange-200',
    ring: 'ring-orange-200',
    screen: 'confirm',
  },
  {
    id: 5,
    chapter: 'Scene 05',
    title: 'Order Placed!',
    subtitle: 'Pickup on its way',
    glow: 'shadow-emerald-200',
    ring: 'ring-emerald-200',
    screen: 'success',
  },
];

const SCENE_DURATION = 3800;
const TRANSITION_MS = 450;

// ── Phone Screens ─────────────────────────────────────────────────────────────

const ScreenDashboard = () => (
  <div className="flex flex-col h-full bg-slate-50">
    {/* Status bar */}
    <div className="flex justify-between items-center px-4 pt-3 pb-1 bg-orange-500">
      <span className="text-[9px] text-orange-100">9:41</span>
      <div className="flex gap-0.5 items-end">
        {[3,4,4,3].map((h,i) => <div key={i} className="w-0.5 rounded-sm bg-white" style={{height:`${h}px`}} />)}
      </div>
    </div>
    {/* Header */}
    <div className="px-4 pt-3 pb-3 bg-gradient-to-r from-orange-500 to-blue-600">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[9px] text-orange-100">Good morning 👋</p>
          <p className="text-sm font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Arjun Sharma</p>
        </div>
        <span className="text-[7px] bg-white/20 text-white px-1.5 py-0.5 rounded-full font-bold">Individual</span>
      </div>
    </div>
    {/* 72hr card */}
    <div className="mx-3 mt-3 mb-2 bg-white rounded-2xl p-3 shadow-sm border border-orange-100">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[8px] text-orange-600 font-bold uppercase tracking-widest">Delivery Promise</p>
        <span className="text-[7px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded-full font-bold">⚡ 72 hrs</span>
      </div>
      <p className="text-xs font-bold text-slate-800">Doorstep pickup &amp; delivery</p>
      <div className="mt-1.5 flex gap-1">
        {['blue','orange','violet','emerald'].map((c,i) => (
          <div key={i} className={`h-1 flex-1 rounded-full bg-${c}-500`} style={{opacity: i===0?1:i===1?0.7:i===2?0.4:0.2}} />
        ))}
      </div>
    </div>
    {/* Quick actions */}
    <div className="px-3 grid grid-cols-2 gap-2">
      <div className="bg-orange-500 rounded-2xl p-3 flex flex-col items-center gap-1 shadow-md shadow-orange-200">
        <Package className="w-4 h-4 text-white" />
        <span className="text-[8px] font-bold text-white">New Order</span>
      </div>
      <div className="bg-white border border-slate-100 rounded-2xl p-3 flex flex-col items-center gap-1 shadow-sm">
        <Truck className="w-4 h-4 text-slate-400" />
        <span className="text-[8px] text-slate-400">My Orders</span>
      </div>
    </div>
    {/* Bottom nav */}
    <div className="mt-auto border-t border-slate-100 bg-white flex items-center justify-around px-4 py-2">
      <HomeIcon className="w-3.5 h-3.5 text-orange-500" />
      <Package className="w-3.5 h-3.5 text-slate-300" />
      <Bell className="w-3.5 h-3.5 text-slate-300" />
      <User className="w-3.5 h-3.5 text-slate-300" />
    </div>
  </div>
);

const ScreenTap = () => (
  <div className="flex flex-col h-full bg-slate-50">
    <div className="px-4 pt-3 pb-3 bg-gradient-to-r from-orange-500 to-blue-600">
      <p className="text-[9px] text-orange-100">Good morning 👋</p>
      <p className="text-sm font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Arjun Sharma</p>
    </div>
    <div className="mx-3 mt-3 mb-2 bg-white rounded-2xl p-3 shadow-sm border border-orange-100">
      <p className="text-[8px] text-orange-600 font-bold uppercase tracking-widest mb-1">Ready to book?</p>
      <p className="text-xs font-bold text-slate-800">Pickup within 24 hrs</p>
    </div>
    <div className="px-3 grid grid-cols-2 gap-2">
      <div className="relative">
        <div className="absolute inset-0 rounded-2xl bg-orange-400/30 animate-ping scale-110" />
        <div className="relative bg-orange-500 rounded-2xl p-3 flex flex-col items-center gap-1 shadow-lg shadow-orange-300 z-10">
          <Package className="w-4 h-4 text-white" />
          <span className="text-[8px] font-bold text-white">New Order</span>
        </div>
        {/* Tap cursor */}
        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md z-20 animate-bounce border border-orange-200">
          <div className="w-2 h-2 bg-orange-500 rounded-full" />
        </div>
      </div>
      <div className="bg-white border border-slate-100 rounded-2xl p-3 flex flex-col items-center gap-1 shadow-sm">
        <Truck className="w-4 h-4 text-slate-400" />
        <span className="text-[8px] text-slate-400">My Orders</span>
      </div>
    </div>
    <div className="mt-auto border-t border-slate-100 bg-white flex items-center justify-around px-4 py-2">
      <HomeIcon className="w-3.5 h-3.5 text-orange-500" />
      <Package className="w-3.5 h-3.5 text-slate-300" />
      <Bell className="w-3.5 h-3.5 text-slate-300" />
      <User className="w-3.5 h-3.5 text-slate-300" />
    </div>
  </div>
);

const ScreenAddress = () => (
  <div className="flex flex-col h-full bg-white">
    <div className="bg-gradient-to-r from-orange-500 to-blue-600 px-4 pt-3 pb-3 flex items-center gap-2">
      <ArrowLeft className="w-3.5 h-3.5 text-orange-100" />
      <p className="text-xs font-bold text-white flex-1 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>New Order</p>
      <div className="w-3.5" />
    </div>
    <div className="px-3 mt-3 space-y-2 flex-1">
      {/* Clothes count */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5">
        <p className="text-[8px] text-slate-400 mb-1.5 font-medium">Total Clothes</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shirt className="w-3.5 h-3.5 text-orange-500" />
            <span className="text-sm font-black text-slate-800">12</span>
          </div>
          <div className="flex gap-1">
            <div className="w-6 h-6 bg-slate-200 rounded-lg flex items-center justify-center text-slate-500 text-xs font-bold">-</div>
            <div className="w-6 h-6 bg-orange-500 rounded-lg flex items-center justify-center text-white text-xs font-bold">+</div>
          </div>
        </div>
      </div>
      {/* Address search */}
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-2.5">
        <p className="text-[8px] text-slate-400 mb-1 font-medium">Pickup Address</p>
        <div className="flex items-center gap-1.5">
          <MapPin className="w-3 h-3 text-orange-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[8.5px] font-bold text-slate-800 leading-tight">Sector 14, Gurgaon</p>
            <p className="text-[7px] text-slate-400">Flat 402, Skyline Apartments</p>
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
        </div>
      </div>
      {/* Type chips */}
      <div className="flex flex-wrap gap-1">
        {['Shirts','Jeans','T-Shirts','Sarees'].map((t,i) => (
          <span key={t} className={`text-[7px] font-bold px-2 py-0.5 rounded-full ${i===0||i===2?'bg-orange-500 text-white':'bg-slate-100 text-slate-500'}`}>{t}</span>
        ))}
      </div>
      <button className="w-full bg-orange-500 rounded-xl py-2 text-[10px] font-bold text-white flex items-center justify-center gap-1 shadow-md shadow-orange-200">
        Continue <ArrowRight className="w-3 h-3" />
      </button>
    </div>
    <div className="border-t border-slate-100 bg-white flex items-center justify-around px-4 py-2">
      <HomeIcon className="w-3.5 h-3.5 text-slate-300" />
      <Package className="w-3.5 h-3.5 text-orange-500" />
      <Bell className="w-3.5 h-3.5 text-slate-300" />
      <User className="w-3.5 h-3.5 text-slate-300" />
    </div>
  </div>
);

const ScreenConfirm = () => (
  <div className="flex flex-col h-full bg-white">
    <div className="bg-gradient-to-r from-orange-500 to-blue-600 px-4 pt-3 pb-3 flex items-center gap-2">
      <ArrowLeft className="w-3.5 h-3.5 text-orange-100" />
      <p className="text-xs font-bold text-white flex-1 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>Confirm Order</p>
      <div className="w-3.5" />
    </div>
    <div className="px-3 mt-3 space-y-2 flex-1">
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 space-y-2">
        {[
          { label: 'Clothes',   value: '12 items', highlight: false },
          { label: 'Type',      value: 'Mixed',    highlight: false },
          { label: 'Pickup',    value: 'Today',    highlight: false },
        ].map((row) => (
          <div key={row.label} className="flex justify-between items-center">
            <span className="text-[8px] text-slate-400 font-medium">{row.label}</span>
            <span className="text-[8px] font-bold text-slate-700">{row.value}</span>
          </div>
        ))}
        {/* 72hr highlight */}
        <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
          <span className="text-[8px] text-slate-500 font-semibold">Delivery</span>
          <span className="text-[8px] font-black text-orange-500 flex items-center gap-0.5">
            <Zap className="w-2 h-2" /> Within 72 hrs
          </span>
        </div>
      </div>
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-2.5 flex items-start gap-2">
        <MapPin className="w-3 h-3 text-orange-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-[7.5px] font-bold text-orange-700">Pickup Address</p>
          <p className="text-[7px] text-slate-500 mt-0.5">Flat 402, Skyline Apts, Sector 14</p>
        </div>
      </div>
      <button className="w-full bg-orange-500 rounded-xl py-2 text-[10px] font-bold text-white shadow-md shadow-orange-200">
        Place Order →
      </button>
    </div>
    <div className="border-t border-slate-100 bg-white flex items-center justify-around px-4 py-2">
      <HomeIcon className="w-3.5 h-3.5 text-slate-300" />
      <Package className="w-3.5 h-3.5 text-orange-500" />
      <Bell className="w-3.5 h-3.5 text-slate-300" />
      <User className="w-3.5 h-3.5 text-slate-300" />
    </div>
  </div>
);

const ScreenSuccess = () => (
  <div className="flex flex-col h-full items-center justify-center px-4 pb-4 bg-white">
    <div className="relative mb-4">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="absolute rounded-full"
          style={{
            width: i%2===0?'4px':'5px', height: i%2===0?'4px':'5px',
            backgroundColor: i%3===0?'#F97316':i%3===1?'#2563EB':'#10B981',
            top:'50%', left:'50%',
            transform:`rotate(${i*45}deg) translateY(-34px) translateX(-50%)`,
          }} />
      ))}
      <div className="w-14 h-14 rounded-full bg-emerald-100 border-2 border-emerald-200 flex items-center justify-center relative z-10">
        <CheckCircle className="w-7 h-7 text-emerald-500" />
      </div>
    </div>
    <p className="text-sm font-black text-slate-900 text-center mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Order Placed! 🎉</p>
    <p className="text-[8px] text-slate-400 text-center mb-3 leading-relaxed">Pickup today · Delivery within 72 hours</p>
    <div className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-2.5 w-full text-center mb-2 flex items-center justify-center gap-1">
      <Zap className="w-3 h-3 text-orange-500" />
      <span className="text-[9px] font-bold text-orange-600">72-hour delivery guaranteed</span>
    </div>
    <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-2 w-full text-center mb-2">
      <p className="text-[7px] text-slate-400 mb-0.5 font-medium">Order ID</p>
      <p className="text-[10px] font-mono font-bold text-blue-600">#JF-IND-2026-0391</p>
    </div>
    <div className="flex gap-2 w-full">
      <button className="flex-1 bg-blue-600 rounded-lg py-1.5 text-[8px] font-bold text-white">Track</button>
      <button className="flex-1 bg-slate-100 rounded-lg py-1.5 text-[8px] font-bold text-slate-500">Share</button>
    </div>
    <div className="flex items-center gap-1 mt-3">
      {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 text-orange-400 fill-orange-400" />)}
      <span className="text-[8px] text-slate-400 ml-1">Rate service</span>
    </div>
    <div className="mt-auto pt-3 w-full border-t border-slate-100 flex items-center justify-around">
      <HomeIcon className="w-3.5 h-3.5 text-slate-300" />
      <Package className="w-3.5 h-3.5 text-orange-500" />
      <Bell className="w-3.5 h-3.5 text-slate-300" />
      <User className="w-3.5 h-3.5 text-slate-300" />
    </div>
  </div>
);

const SCREEN_MAP = {
  dashboard: ScreenDashboard,
  tap: ScreenTap,
  address: ScreenAddress,
  confirm: ScreenConfirm,
  success: ScreenSuccess,
};

// ── Cinematic Demo ────────────────────────────────────────────────────────────
const CinematicDemo = () => {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('show');
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  const advance = useCallback(() => {
    setPhase('out');
    setTimeout(() => {
      setIdx(p => (p + 1) % SCENES.length);
      setPhase('in');
      setTimeout(() => setPhase('show'), TRANSITION_MS);
    }, TRANSITION_MS);
  }, []);

  useEffect(() => {
    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const pct = Math.min((ts - startRef.current) / SCENE_DURATION, 1);
      setProgress(pct);
      if (pct < 1) rafRef.current = requestAnimationFrame(tick);
      else { advance(); startRef.current = null; setProgress(0); }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [idx, advance]);

  const scene = SCENES[idx];
  const Screen = SCREEN_MAP[scene.screen];

  const screenStyle = {
    opacity: phase === 'show' ? 1 : 0,
    transform: phase === 'out' ? 'translateY(10px) scale(0.98)' : phase === 'in' ? 'translateY(-6px) scale(1.01)' : 'none',
    transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease`,
  };

  return (
    <div className="flex flex-col items-center select-none w-full">
      {/* Scene label */}
      <div className="flex items-center gap-3 mb-4" style={{ opacity: phase === 'show' ? 1 : 0, transition: `opacity ${TRANSITION_MS}ms ease` }}>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
          <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest">Live Demo</span>
        </div>
        <span className="text-slate-300">·</span>
        <span className="text-[10px] font-mono text-slate-400">{scene.chapter}</span>
      </div>

      {/* Scene title */}
      <div className="text-center mb-5" style={{ opacity: phase === 'show' ? 1 : 0, transform: phase === 'show' ? 'translateY(0)' : 'translateY(6px)', transition: `all ${TRANSITION_MS}ms ease` }}>
        <h3 className="text-xl font-black text-slate-800 mb-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{scene.title}</h3>
        <p className="text-sm text-slate-400">{scene.subtitle}</p>
      </div>

      {/* Phone */}
      <div className="relative">
        {/* Glow */}
        <div className={`absolute inset-0 rounded-[40px] blur-2xl scale-105 transition-all duration-700 shadow-2xl ${scene.glow}`} style={{ opacity: 0.6 }} />

        {/* Phone shell */}
        <div className={`relative w-[200px] h-[410px] rounded-[36px] bg-white border-4 border-slate-200 overflow-hidden shadow-2xl ring-4 ${scene.ring} transition-all duration-500`}>
          {/* Dynamic island */}
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-14 h-4 bg-slate-900 rounded-2xl z-20 flex items-center justify-center gap-1">
            <div className="w-1 h-1 bg-slate-600 rounded-full" />
            <div className="w-1.5 h-1.5 bg-slate-700 rounded-full" />
          </div>

          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-slate-200 z-30">
            <div className="h-full bg-orange-500 transition-none" style={{ width: `${progress * 100}%` }} />
          </div>

          <div className="w-full h-full pt-7" style={screenStyle}>
            <Screen />
          </div>
        </div>

        {/* Side buttons */}
        <div className="absolute right-[-3px] top-24 w-1 h-10 bg-slate-300 rounded-l-sm" />
        <div className="absolute left-[-3px] top-20 w-1 h-6 bg-slate-300 rounded-r-sm" />
        <div className="absolute left-[-3px] top-32 w-1 h-10 bg-slate-300 rounded-r-sm" />
      </div>

      {/* Dot timeline */}
      <div className="flex items-center gap-1.5 mt-5">
        {SCENES.map((s, i) => (
          <button key={s.id}
            onClick={() => { setIdx(i); startRef.current = null; setProgress(0); }}
            className={`transition-all duration-300 rounded-full ${i === idx ? 'w-6 h-2 bg-orange-500' : 'w-2 h-2 bg-slate-300 hover:bg-slate-400'}`}
          />
        ))}
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Doorstep pickup in under 60 seconds</p>
    </div>
  );
};

// ─── ADDRESS SEARCH ────────────────────────────────────────────────────────────
const AddressSearch = ({ value, onChange, onSelect }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const timerRef = useRef(null);
  const containerRef = useRef(null);
  const selectingRef = useRef(false);
  const autocompleteService = useRef(null);
  const geocoderRef = useRef(null);

  useEffect(() => {
    const initGoogle = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        geocoderRef.current = new window.google.maps.Geocoder();
      }
    };
    if (window.google) { initGoogle(); }
    else {
      const interval = setInterval(() => {
        if (window.google) { initGoogle(); clearInterval(interval); }
      }, 300);
      return () => clearInterval(interval);
    }
  }, []);

  const search = useCallback((q) => {
    if (!q || q.length < 2) { setResults([]); setShowDropdown(false); return; }
    if (!autocompleteService.current) { setResults([]); return; }
    setSearching(true);
    // Timeout fallback — if Google doesn't respond in 5s, stop spinner
    const timeout = setTimeout(() => setSearching(false), 5000);
    autocompleteService.current.getPlacePredictions(
      { input: q, componentRestrictions: { country: 'in' }, types: ['geocode', 'establishment'] },
      (predictions, status) => {
        clearTimeout(timeout);
        setSearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
          setResults(predictions);
          setShowDropdown(true);
        } else {
          setResults([]);
          setShowDropdown(false);
        }
      }
    );
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (selectingRef.current) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(v), 300);
  };

  const handleSelect = (prediction) => {
    selectingRef.current = false;
    const addr = prediction.description;
    setQuery(addr);
    onChange(addr);
    setShowDropdown(false);
    setResults([]);
    // Geocode to get lat/lon
    if (geocoderRef.current) {
      geocoderRef.current.geocode({ placeId: prediction.place_id }, (res, status) => {
        if (status === 'OK' && res[0]) {
          const loc = res[0].geometry.location;
          onSelect({ address: addr, lat: loc.lat(), lon: loc.lng() });
        } else {
          onSelect({ address: addr, lat: null, lon: null });
        }
      });
    } else {
      onSelect({ address: addr, lat: null, lon: null });
    }
  };

  const handleGPS = () => {
    if (!navigator.geolocation) { toast.error('Location not supported by this browser'); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        if (geocoderRef.current) {
          geocoderRef.current.geocode({ location: { lat: latitude, lng: longitude } }, (res, status) => {
            setGpsLoading(false);
            if (status === 'OK' && res[0]) {
              const addr = res[0].formatted_address;
              setQuery(addr);
              onChange(addr);
              onSelect({ address: addr, lat: latitude, lon: longitude });
              toast.success('Location detected!');
            } else {
              toast.error('Could not fetch address — try searching manually');
            }
          });
        } else {
          setGpsLoading(false);
          toast.error('Maps not loaded yet — try again in a moment');
        }
      },
      (err) => {
        setGpsLoading(false);
        if (err.code === 1) toast.error('Location permission denied. Please allow access in browser settings.');
        else if (err.code === 2) toast.error('Location unavailable — try searching manually');
        else toast.error('Location timed out — try searching manually');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* GPS current location button */}
      <button
        type="button"
        onClick={handleGPS}
        disabled={gpsLoading}
        className="w-full flex items-center gap-2.5 mb-2 px-4 py-2.5 bg-orange-50 hover:bg-orange-100 border border-orange-200 hover:border-orange-300 rounded-xl text-sm font-semibold text-orange-600 transition-colors disabled:opacity-60"
      >
        {gpsLoading ? <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" /> : <MapPin className="w-4 h-4 flex-shrink-0" />}
        {gpsLoading ? 'Detecting location...' : 'Use current location'}
        {!gpsLoading && <span className="ml-auto text-[10px] font-normal text-orange-400">GPS</span>}
      </button>

      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 h-px bg-slate-200" />
        <span className="text-[10px] text-slate-400 font-medium">or search manually</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      <div className="relative">
        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search address, area, landmark..."
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 hover:border-orange-300 focus:border-orange-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none"
        />
        {searching && <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
        {query && !searching && (
          <button type="button" onClick={() => { setQuery(''); onChange(''); setResults([]); setShowDropdown(false); }}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-52 overflow-y-auto">
          {results.map((item) => (
            <button key={item.place_id} type="button"
              onPointerDown={() => { selectingRef.current = true; }}
              onClick={() => handleSelect(item)}
              className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-orange-50 hover:text-orange-700 border-b border-slate-50 last:border-0 transition-colors">
              <div className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-slate-800">{item.structured_formatting?.main_text}</p>
                  <p className="text-xs text-slate-400">{item.structured_formatting?.secondary_text}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── OTP SCREEN (shared with PG, reused here) ────────────────────────────────
const OTPScreen = ({ email, customerId, onVerified, otpPurpose }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);
  const cooldownRef = useRef(null);

  const startCooldown = () => {
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => { inputRefs.current[0]?.focus(); return () => clearInterval(cooldownRef.current); }, []);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[i] = val;
    setOtp(next);
    if (val && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) inputRefs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) { setOtp(text.split('')); inputRefs.current[5]?.focus(); }
    e.preventDefault();
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/individual/verify-otp`, { customer_id: customerId, otp: code });
      onVerified(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendLoading(true);
    try {
      await axios.post(`${API}/individual/resend-otp`, { customer_id: customerId });
      toast.success('New OTP sent!');
      startCooldown();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to resend OTP'); }
    finally { setResendLoading(false); }
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-5">
        <ShieldCheck className="w-7 h-7 text-orange-500" />
      </div>
      <h2 className="text-xl font-black text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Verify your email</h2>
      <p className="text-sm text-slate-500 text-center mb-1">We sent a 6-digit code to</p>
      <p className="text-sm font-bold text-orange-600 mb-6">{email}</p>

      <div className="flex gap-2 mb-6" onPaste={handlePaste}>
        {otp.map((digit, i) => (
          <input key={i} ref={el => inputRefs.current[i] = el}
            type="text" inputMode="numeric" maxLength={1} value={digit}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            className={`w-11 text-center text-xl font-black border-2 rounded-xl outline-none transition-all
              ${digit ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 bg-slate-50 text-slate-800'}
              focus:border-orange-500 focus:bg-orange-50`}
            style={{ height: '52px' }}
          />
        ))}
      </div>

      <button onClick={handleVerify} disabled={loading || otp.join('').length !== 6}
        className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all text-sm mb-4">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4" /> Verify & Continue</>}
      </button>

      <button onClick={handleResend} disabled={resendCooldown > 0 || resendLoading}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-orange-600 disabled:opacity-50 transition-colors">
        {resendLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
      </button>
    </div>
  );
};

// ─── PROFILE COMPLETION MODAL (for new Google users) ─────────────────────────
const ProfileCompletion = ({ token, customer, onCompleted }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    phone: '', address_line1: '', address_line2: '',
    latitude: null, longitude: null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.address_line1) { toast.error('Please search and select your address'); return; }
    if (!data.address_line2.trim()) { toast.error('Please enter your flat/house/floor/tower details'); return; }
    setLoading(true);
    try {
      const r = await axios.put(`${API}/individual/profile`, {
        phone: data.phone,
        address_line1: data.address_line1,
        address_line2: data.address_line2,
        latitude: data.latitude || undefined,
        longitude: data.longitude || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onCompleted(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save details'); }
    finally { setLoading(false); }
  };

  const inputClass = "w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 hover:border-orange-300 focus:border-orange-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HomeIcon className="w-7 h-7 text-orange-500" />
          </div>
          <h2 className="text-xl font-black text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Almost there!</h2>
          <p className="text-sm text-slate-500">Hi {customer?.full_name?.split(' ')[0]}, a few more details to complete your profile</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="tel" placeholder="Your phone number" value={data.phone}
                onChange={e => setData({...data, phone: e.target.value})}
                required className={inputClass} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Delivery Address <span className="text-xs text-slate-400 font-normal">(use GPS or search)</span>
            </label>
            <AddressSearch
              value={data.address_line1}
              onChange={v => setData({...data, address_line1: v})}
              onSelect={({ address, lat, lon }) => setData({...data, address_line1: address, latitude: lat, longitude: lon})}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              Flat / House / Tower / Floor <span className="text-xs text-red-400 font-normal">(required)</span>
            </label>
            <div className="relative">
              <HomeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="e.g. Flat 402, Tower B, 4th Floor, Landmark..." value={data.address_line2}
                onChange={e => setData({...data, address_line2: e.target.value})}
                required className={inputClass} />
            </div>
          </div>

          <button type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all text-sm mt-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Save & Continue <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── FORGOT PASSWORD ──────────────────────────────────────────────────────────
const ForgotPasswordScreen = ({ onBack }) => {
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef(null);

  const startTimer = () => {
    setResendTimer(60);
    timerRef.current = setInterval(() => {
      setResendTimer(t => { if (t <= 1) { clearInterval(timerRef.current); return 0; } return t - 1; });
    }, 1000);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/individual/forgot-password`, { email });
      toast.success('OTP sent! Check your inbox.');
      setStep('otp');
      startTimer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email not found');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await axios.post(`${API}/individual/forgot-password`, { email });
      toast.success('OTP resent!');
      startTimer();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to resend'); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/individual/reset-password`, { email, otp, new_password: newPassword });
      toast.success('Password reset successfully! Please login.');
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired OTP');
    } finally { setLoading(false); }
  };

  const inputClass = "w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none";

  return (
    <div className="w-full">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors text-sm mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Login
      </button>

      <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
        <Lock className="w-6 h-6 text-blue-600" />
      </div>
      <h2 className="text-2xl font-black text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Reset Password</h2>
      <p className="text-sm text-slate-500 mb-6">
        {step === 'email' ? 'Enter your registered email to receive a reset code.' : `Enter the 4-digit code sent to ${email}`}
      </p>

      {step === 'email' && (
        <form onSubmit={handleSendOTP} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="email" placeholder="your@email.com" value={email}
                onChange={e => setEmail(e.target.value)} required className={inputClass} />
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
            <p className="text-xs text-amber-700 font-medium">
              📧 Please check your <strong>spam / junk folder</strong> if you don't receive the email within a minute.
            </p>
          </div>
          <button type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all text-sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Reset Code <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      )}

      {step === 'otp' && (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">4-Digit OTP</label>
            <div className="relative">
              <ShieldCheck className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="Enter 4-digit code" value={otp} maxLength={4}
                onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} required className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">New Password</label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="password" placeholder="Create new password (min 6 chars)" value={newPassword}
                onChange={e => setNewPassword(e.target.value)} required minLength={6} className={inputClass} />
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
            <p className="text-xs text-amber-700 font-medium">
              📧 Didn't receive the code? Check your <strong>spam / junk folder</strong> first.
            </p>
          </div>
          <button type="submit" disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all text-sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Reset Password <ArrowRight className="w-4 h-4" /></>}
          </button>
          <div className="text-center">
            <button type="button" onClick={handleResend} disabled={resendTimer > 0 || loading}
              className={`text-xs font-semibold transition-colors ${resendTimer > 0 ? 'text-slate-300 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700'}`}>
              {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// ─── INDIVIDUAL AUTH ───────────────────────────────────────────────────────────
const IndividualAuth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  // phase: 'form' | 'otp' | 'profile_completion'
  const [phase, setPhase] = useState('form');
  const [pendingCustomer, setPendingCustomer] = useState(null); // { id, email }
  const [googleSession, setGoogleSession] = useState(null); // { token, customer } for profile completion
  const [loginData, setLoginData] = useState({ phone: '', password: '' });
  const [registerData, setRegisterData] = useState({
    full_name: '', email: '', phone: '', password: '',
    address_line1: '', address_line2: '', city: '', state: '', pincode: '',
    latitude: null, longitude: null, referred_by_code: ''
  });
  const [refCodeAutoFilled, setRefCodeAutoFilled] = useState(false);

  useEffect(() => {
    const existing = localStorage.getItem('individualToken');
    if (existing) navigate('/individual/dashboard');
  }, [navigate]);

  // Deep-link: read ?ref= param and pre-fill referral code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setRegisterData(prev => ({ ...prev, referred_by_code: refCode.toUpperCase() }));
      setRefCodeAutoFilled(true);
      setActiveTab('register');
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await axios.post(`${API}/individual/login`, loginData);
      localStorage.setItem('individualToken', r.data.token);
      localStorage.setItem('individualData', JSON.stringify(r.data.customer));
      toast.success('Welcome back!');
      navigate('/individual/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.error === 'email_not_verified') {
        setPendingCustomer({ id: detail.customer_id, email: detail.email });
        setPhase('otp');
        toast('Please verify your email to continue', { icon: '📧' });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Login failed');
      }
    }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.address_line1) { toast.error('Please search and select your address'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/individual/register`, registerData);
      if (r.data.email && r.data.requires_otp) {
        // Has email — go through OTP verification
        setPendingCustomer({ id: r.data.customer_id, email: r.data.email });
        setPhase('otp');
        toast.success(`OTP sent to ${r.data.email}`);
      } else {
        // No email — account is active immediately
        localStorage.setItem('individualToken', r.data.token);
        localStorage.setItem('individualData', JSON.stringify(r.data.customer));
        toast.success('Welcome to JustFold!');
        navigate('/individual/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join(', '));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Registration failed');
      }
    }
    finally { setLoading(false); }
  };

  const handleOTPVerified = (data) => {
    localStorage.setItem('individualToken', data.token);
    localStorage.setItem('individualData', JSON.stringify(data.customer));
    toast.success('Email verified! Welcome to JustFold!');
    navigate('/individual/dashboard');
  };

  // Google login via access_token flow
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        const r = await axios.post(`${API}/auth/google/individual`, { access_token: tokenResponse.access_token });
        localStorage.setItem('individualToken', r.data.token);
        localStorage.setItem('individualData', JSON.stringify(r.data.customer));
        if (r.data.needs_profile_completion) {
          setGoogleSession({ token: r.data.token, customer: r.data.customer });
          setPhase('profile_completion');
        } else {
          toast.success('Welcome back!');
          navigate('/individual/dashboard');
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Google login failed');
      } finally { setGoogleLoading(false); }
    },
    onError: () => { toast.error('Google login cancelled'); setGoogleLoading(false); },
  });

  const handleProfileCompleted = (updatedCustomer) => {
    localStorage.setItem('individualData', JSON.stringify(updatedCustomer));
    toast.success('Profile complete! Welcome to JustFold!');
    navigate('/individual/dashboard');
  };

  const inputClass = "w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none";

  return (
    <div className="min-h-screen bg-white flex" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── LEFT: Cinematic Demo Panel ── */}
      <div className="hidden lg:flex flex-col flex-1 relative overflow-hidden items-center justify-center px-8 py-16 bg-gradient-to-br from-orange-50 via-slate-50 to-blue-50">
        {/* Decorative blobs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-orange-100 rounded-full blur-[80px] opacity-70" />
          <div className="absolute bottom-10 right-0 w-64 h-64 bg-blue-100 rounded-full blur-[70px] opacity-60" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-48 h-48 bg-violet-100 rounded-full blur-[60px] opacity-50" />
          <div className="absolute inset-0 opacity-[0.4]" style={{ backgroundImage: 'radial-gradient(circle, #CBD5E1 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        </div>

        <div className="relative z-10 flex flex-col items-center max-w-sm w-full">
          <img src="/logo.png" alt="JustFold" className="h-10 mb-8 object-contain" />
          <CinematicDemo />

          {/* Testimonial */}
          <div className="mt-8 bg-white rounded-2xl p-4 shadow-sm border border-slate-100 w-full">
            <div className="flex gap-1 mb-2">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 text-orange-400 fill-orange-400" />)}
            </div>
            <p className="text-sm text-slate-600 leading-relaxed italic">"So convenient! They pick up from my apartment and deliver fresh within a day and a half. Highly recommended!"</p>
            <p className="text-xs text-slate-400 mt-2 font-medium">— Arjun S., Sector 14 Gurgaon</p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden lg:block w-px bg-slate-100 self-stretch" />

      {/* ── RIGHT: Phase-based content ── */}
      <div className="flex flex-col flex-1 lg:max-w-md items-center justify-center px-6 py-8 lg:px-12 bg-white overflow-y-auto">

        {/* Forgot Password phase */}
        {phase === 'forgot_password' && (
          <ForgotPasswordScreen onBack={() => setPhase('form')} />
        )}

        {/* OTP phase */}
        {phase === 'otp' && (
          <>
            <div className="w-full mb-6">
              <button onClick={() => setPhase('form')} className="flex items-center gap-2 text-slate-400 hover:text-orange-600 transition-colors text-sm">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            </div>
            <OTPScreen
              email={pendingCustomer?.email}
              customerId={pendingCustomer?.id}
              otpPurpose="individual"
              onVerified={handleOTPVerified}
            />
            <p className="text-xs text-slate-300 text-center mt-8">Check your spam folder if you don't see the email.</p>
          </>
        )}

        {/* Auth form phase */}
        {phase === 'form' && (
          <>
        <div className="w-full mb-6">
          <button onClick={() => navigate('/customer/login')} className="flex items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors text-sm mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-3 lg:hidden">
            <img src="/logo.png" alt="JustFold" className="h-8 object-contain" />
          </div>
          <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-3 py-1 mb-3">
            <HomeIcon className="w-3.5 h-3.5 text-orange-500" />
            <span className="text-xs font-bold text-orange-600">Individual Customer</span>
          </div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {activeTab === 'login' ? 'Welcome back 👋' : 'Create account ✨'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {activeTab === 'login' ? 'Login to your individual account' : 'Join JustFold — doorstep delivery in 72 hrs'}
          </p>
        </div>

        {/* Google button */}
        <div className="w-full mb-4">
          <button type="button" onClick={() => googleLogin()} disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-700 font-semibold py-3 rounded-xl transition-all text-sm shadow-sm">
            {googleLoading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            }
            Continue with Google
          </button>
          <div className="flex items-center gap-3 mt-4 mb-2">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[11px] text-slate-400 font-medium">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
        </div>

        {/* Tab toggle */}
        <div className="w-full flex bg-slate-100 rounded-2xl p-1 mb-6">
          {['login', 'register'].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2.5 text-sm font-bold rounded-xl transition-all ${
                activeTab === tab ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'text-slate-500 hover:text-slate-700'
              }`}>
              {tab === 'login' ? 'Login' : 'Register'}
            </button>
          ))}
        </div>

        {/* ── LOGIN ── */}
        {activeTab === 'login' && (
          <form onSubmit={handleLogin} className="w-full space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="tel" placeholder="Enter your phone number" value={loginData.phone}
                  onChange={e => setLoginData({...loginData, phone: e.target.value})}
                  required className={inputClass} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="password" placeholder="Enter your password" value={loginData.password}
                  onChange={e => setLoginData({...loginData, password: e.target.value})}
                  required className={inputClass} />
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-blue-200 hover:-translate-y-0.5 text-sm mt-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Login to JustFold <ArrowRight className="w-4 h-4" /></>}
            </button>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">
                Don't have an account?{' '}
                <button type="button" onClick={() => setActiveTab('register')} className="text-blue-600 hover:text-blue-700 font-semibold">Register here</button>
              </p>
              <button type="button" onClick={() => setPhase('forgot_password')} className="text-xs text-slate-400 hover:text-orange-600 font-semibold transition-colors">
                Forgot password?
              </button>
            </div>
          </form>
        )}

        {/* ── REGISTER ── */}
        {activeTab === 'register' && (
          <form onSubmit={handleRegister} className="w-full space-y-3.5">
            {[
              { label: 'Full Name',     key: 'full_name', icon: User,  type: 'text',     placeholder: 'Your full name' },
              { label: 'Email Address (Optional)', key: 'email', icon: Mail, type: 'email', placeholder: 'your@email.com (optional)' },
              { label: 'Phone Number',  key: 'phone',     icon: Phone, type: 'tel',      placeholder: 'Your phone number' },
              { label: 'Password',      key: 'password',  icon: Lock,  type: 'password', placeholder: 'Create a password' },
            ].map(({ label, key, icon: Icon, type, placeholder }) => (
              <div key={key}>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
                <div className="relative">
                  <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type={type} placeholder={placeholder} value={registerData[key]}
                    onChange={e => setRegisterData({...registerData, [key]: e.target.value})}
                    required className={inputClass} />
                </div>
              </div>
            ))}

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Address <span className="text-xs text-slate-400 font-normal">(use GPS or search)</span>
              </label>
              <AddressSearch
                value={registerData.address_line1}
                onChange={(v) => setRegisterData({...registerData, address_line1: v})}
                onSelect={({ address, lat, lon }) => setRegisterData({...registerData, address_line1: address, latitude: lat, longitude: lon})}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Flat / House / Tower / Floor <span className="text-xs text-red-400 font-normal">(required)</span>
              </label>
              <div className="relative">
                <HomeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" placeholder="e.g. Flat 402, Tower B, 4th Floor, Landmark..." value={registerData.address_line2}
                  onChange={e => setRegisterData({...registerData, address_line2: e.target.value})}
                  required className={inputClass} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">City</label>
                <input type="text" placeholder="City" value={registerData.city}
                  onChange={e => setRegisterData({...registerData, city: e.target.value})}
                  className="w-full px-3 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Pincode</label>
                <input type="text" placeholder="Pincode" value={registerData.pincode}
                  onChange={e => setRegisterData({...registerData, pincode: e.target.value})}
                  className="w-full px-3 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none" />
              </div>
            </div>

            {/* Referral code */}
            <div>
              {refCodeAutoFilled && (
                <div className="mb-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                  <span className="text-emerald-600 text-sm font-semibold">🎉 You'll get 25% off your first order!</span>
                </div>
              )}
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Referral Code <span className="font-normal text-slate-400">(optional)</span></label>
              <input
                type="text"
                placeholder="Enter a friend's referral code"
                value={registerData.referred_by_code}
                onChange={e => setRegisterData({...registerData, referred_by_code: e.target.value.toUpperCase()})}
                className="w-full px-3 py-3 bg-slate-50 border border-slate-200 hover:border-orange-300 focus:border-orange-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none font-mono tracking-widest"
              />
              {!refCodeAutoFilled && <p className="text-xs text-slate-400 mt-1">Have a friend's code? Enter it to get 25% off your first order</p>}
            </div>

            <button type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-orange-200 hover:-translate-y-0.5 text-sm mt-1">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create Account <Zap className="w-4 h-4" /></>}
            </button>
            <p className="text-xs text-slate-400 text-center">
              Already have an account?{' '}
              <button type="button" onClick={() => setActiveTab('login')} className="text-blue-600 hover:text-blue-700 font-semibold">Login here</button>
            </p>
          </form>
        )}

        {/* Mobile teaser */}
        <div className="lg:hidden mt-8 w-full bg-orange-50 border border-orange-200 rounded-2xl px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Zap className="w-4 h-4 text-orange-500" />
          </div>
          <p className="text-xs text-slate-500">Doorstep pickup + 72-hour delivery. Book in under 60 seconds.</p>
        </div>

        <p className="text-xs text-slate-300 text-center mt-6">By continuing you agree to JustFold's terms of service.</p>
          </>
        )}
      </div>

      {/* Profile completion modal for new Google users */}
      {phase === 'profile_completion' && googleSession && (
        <ProfileCompletion
          token={googleSession.token}
          customer={googleSession.customer}
          onCompleted={handleProfileCompleted}
        />
      )}
    </div>
  );
};

export default IndividualAuth;
